import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { dataOverviewApi } from '../../services/endpoints';
import { DatasetOverview } from '../../services/types';
import { saveNavigationState, restoreNavigationState } from '../../utils/navigationStateManager';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Stepper } from '../ui/Stepper';
import { CaretDown, CaretRight, WarningCircle, ArrowLeft, ArrowRight, CircleNotch } from '@phosphor-icons/react';

interface LocationState {
  projectIds?: string[];
  organism?: 'human' | 'mouse';
}

/** Extract the backend's `detail` message from a caught error, if present. */
function getErrorDetail(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
  }
  return fallback;
}

const DatasetPreparation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const state = (location.state as LocationState) || 
    (restoreNavigationState('dataset-preparation') as LocationState) || {};

  const [dataset, setDataset] = useState<DatasetOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  const [minVariance, setMinVariance] = useState<number>(0);
  const [topGenes, setTopGenes] = useState<number | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);

  // Metadata display states
  const [expandedSections, setExpandedSections] = useState({
    tissues: true,
    conditions: true,
    samples: false,
  });

  useEffect(() => {
    if (state.projectIds && state.organism) {
      saveNavigationState('dataset-preparation', state);
    }
    loadDataset();
    // Intentionally mount-only: `state` is recomputed fresh every render
    // (location.state / sessionStorage fallback chain), so including it or
    // loadDataset would re-trigger the fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDataset = async () => {
    console.log('[DatasetPreparation] loadDataset called with state:', state);
    
    if (!state?.projectIds || !state?.organism) {
      console.error('[DatasetPreparation] Missing required state');
      setError('No dataset selected. Please go back and select a dataset.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const projectId = state.projectIds[0];
      console.log(`[DatasetPreparation] Loading dataset ${projectId}...`);
      
      // Check if h5ad file exists, if not convert it
      console.log(`[DatasetPreparation] Fetching overview for ${projectId}...`);
      const overview = await dataOverviewApi.getOverview(projectId, state.organism);
      console.log(`[DatasetPreparation] Overview result:`, {
        has_h5ad: overview.data.has_h5ad,
        h5ad_path: overview.data.h5ad_path,
        gene_count: overview.data.gene_count,
        sample_count: overview.data.sample_count,
      });
      
      if (!overview.data) {
        throw new Error(`Failed to get overview for ${projectId}`);
      }
      
      if (!overview.data.has_h5ad) {
        console.log(`[DatasetPreparation] H5ad not found, starting conversion for ${projectId}...`);
        const convertResult = await dataOverviewApi.convertToH5ad({
          project_id: projectId,
          organism: state.organism as 'human' | 'mouse',
        });
        console.log(`[DatasetPreparation] Conversion result:`, convertResult.data);
        
        // Wait a bit and reload to get updated overview
        console.log('[DatasetPreparation] Waiting 500ms before reloading overview...');
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('[DatasetPreparation] Reloading overview after conversion...');
        const response = await dataOverviewApi.getOverview(projectId, state.organism);
        console.log('[DatasetPreparation] Reloaded overview:', {
          has_h5ad: response.data.has_h5ad,
          h5ad_path: response.data.h5ad_path,
        });
        
        if (!response.data) {
          throw new Error(`Failed to reload overview after conversion for ${projectId}`);
        }
        setDataset(response.data);
        console.log('[DatasetPreparation] Dataset set successfully');
      } else {
        console.log('[DatasetPreparation] H5ad exists, setting dataset directly');
        setDataset(overview.data);
      }
    } catch (err: unknown) {
      console.error('[DatasetPreparation] Failed to load dataset:', err);
      setError(getErrorDetail(err, 'Failed to load dataset'));
    } finally {
      setLoading(false);
      console.log('[DatasetPreparation] loadDataset completed');
    }
  };

  const handleApplyFilters = async () => {
    if (!dataset) return;

    try {
      setIsFiltering(true);
      setError(null);

      await dataOverviewApi.filterDataset({
        project_id: dataset.project_id,
        organism: state.organism!,
        remove_samples: selectedSamples,
        min_gene_variance: minVariance > 0 ? minVariance : undefined,
        top_variable_genes: topGenes || undefined,
      });

      const updatedDataset = await dataOverviewApi.getOverview(
        dataset.project_id,
        state.organism!
      );
      setDataset(updatedDataset.data);
      setSelectedSamples([]);
      setMinVariance(0);
      setTopGenes(null);
    } catch (err: unknown) {
      console.error('Failed to apply filters:', err);
      setError(getErrorDetail(err, 'Failed to apply filters'));
    } finally {
      setIsFiltering(false);
    }
  };

  const handleProceedToSignatures = () => {
    if (!dataset) return;

    navigate('/select-signature', {
      state: {
        dataType: 'public',
        datasetId: dataset.project_id,
        organism: state.organism,
        tissues: dataset.metadata_preview?.tissues || [],
        geneCount: dataset.gene_count,
        sampleCount: dataset.sample_count,
        fileData: {
          filename: `${dataset.project_id}`,
          gene_count: dataset.gene_count,
          sample_count: dataset.sample_count,
        },
      },
    });
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleSampleSelection = (sampleId: string) => {
    setSelectedSamples((prev) =>
      prev.includes(sampleId)
        ? prev.filter((id) => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error && !dataset) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8">
            <div className="flex items-start gap-4">
              <WarningCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-foreground mb-2">
                  Error loading dataset
                </h2>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => navigate('/public-data')}>
                  Back to dataset selection
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!dataset) return null;

  const tissues = dataset.metadata_preview?.tissues || [];
  const conditions = dataset.metadata_preview?.conditions || [];
  const tissueCounts = dataset.metadata_preview?.tissue_counts || {};
  const conditionCounts = dataset.metadata_preview?.condition_counts || {};
  const sampleIds = dataset.metadata_preview?.sample_ids || [];

  const steps = [
    { label: 'Upload', status: 'completed' as const },
    { label: 'Validate', status: 'current' as const },
    { label: 'Select Signature', status: 'upcoming' as const },
    { label: 'Run', status: 'upcoming' as const }
  ];

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Stepper steps={steps} />
        
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/public-data')}
            className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-2 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dataset selection
          </button>
          <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-3">
            Dataset preparation
          </span>
          <h1 className="text-foreground mb-2">
            Review your dataset.
          </h1>
          <p className="text-muted-foreground">
            Filter and validate before selecting a signature
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <Card className="mb-6 p-4 bg-destructive/10 border-destructive/20">
            <div className="flex items-start gap-3">
              <WarningCircle className="w-5 h-5 text-destructive mt-0.5" />
              <div className="flex-1">
                <p className="text-destructive">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss"
              >
                <span className="text-sm leading-none">×</span>
              </button>
            </div>
          </Card>
        )}

        {/* Dataset Overview Card */}
        <Card className="p-6 mb-6">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-4">
            Dataset overview
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#EAEAEA] border border-border rounded-xl overflow-hidden mb-6">
            <div className="p-4 bg-background">
              <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Project ID</p>
              <p className="text-sm font-mono font-medium text-foreground truncate">
                {dataset.project_id}
              </p>
            </div>
            <div className="p-4 bg-background">
              <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Organism</p>
              <p className="text-xl font-bold text-teal-600 font-display tabular-nums capitalize">
                {dataset.organism}
              </p>
            </div>
            <div className="p-4 bg-background">
              <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Genes</p>
              <p className="text-2xl font-bold text-teal-600 font-display tabular-nums">
                {dataset.gene_count?.toLocaleString() || 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-background">
              <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Samples</p>
              <p className="text-2xl font-bold text-teal-600 font-display tabular-nums">
                {dataset.sample_count?.toLocaleString() || 'N/A'}
              </p>
            </div>
          </div>

          {/* Merge Information */}
          {dataset.processing_summary?.merge_operation && (
            <div className="p-4 border border-border rounded-lg">
              <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-3">
                Merged dataset
              </p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>
                  <span className="font-medium text-foreground">Source datasets:</span>{' '}
                  {dataset.processing_summary.n_datasets_merged} merged
                </p>
                <p>
                  <span className="font-medium text-foreground">Overlapping genes:</span>{' '}
                  {dataset.processing_summary.overlapping_genes?.toLocaleString()}
                </p>
                {dataset.processing_summary.source_datasets && (
                  <p>
                    <span className="font-medium text-foreground">IDs:</span>{' '}
                    {dataset.processing_summary.source_datasets}
                  </p>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Metadata Sections */}
        <Card className="p-6 mb-6">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-4">
            Dataset metadata
          </p>

          {/* Tissues Section */}
          <div className="border-b border-border pb-4 mb-4">
            <button
              onClick={() => toggleSection('tissues')}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="font-semibold text-foreground">
                Tissues <span className="font-mono text-sm font-normal text-muted-foreground">({tissues.length})</span>
              </h3>
              {expandedSections.tissues ? (
                <CaretDown className="w-5 h-5 text-muted-foreground" />
              ) : (
                <CaretRight className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedSections.tissues && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                {tissues.map((tissue) => (
                  <div
                    key={tissue}
                    className="border border-border rounded px-3 py-2 flex justify-between items-center bg-background"
                  >
                    <span className="text-sm text-foreground">{tissue}</span>
                    {tissueCounts[tissue] && (
                      <span className="font-mono text-xs text-muted-foreground">
                        {tissueCounts[tissue]}
                      </span>
                    )}
                  </div>
                ))}
                {tissues.length === 0 && (
                  <p className="text-muted-foreground text-sm col-span-full">
                    No tissue information available
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Conditions Section */}
          <div className="border-b border-border pb-4 mb-4">
            <button
              onClick={() => toggleSection('conditions')}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="font-semibold text-foreground">
                Conditions <span className="font-mono text-sm font-normal text-muted-foreground">({conditions.length})</span>
              </h3>
              {expandedSections.conditions ? (
                <CaretDown className="w-5 h-5 text-muted-foreground" />
              ) : (
                <CaretRight className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedSections.conditions && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                {conditions.map((condition) => (
                  <div
                    key={condition}
                    className="border border-border rounded px-3 py-2 flex justify-between items-center bg-background"
                  >
                    <span className="text-sm text-foreground">{condition}</span>
                    {conditionCounts[condition] && (
                      <span className="font-mono text-xs text-muted-foreground">
                        {conditionCounts[condition]}
                      </span>
                    )}
                  </div>
                ))}
                {conditions.length === 0 && (
                  <p className="text-muted-foreground text-sm col-span-full">
                    No condition information available
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Samples Section */}
          <div>
            <button
              onClick={() => toggleSection('samples')}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="font-semibold text-foreground">
                Samples <span className="font-mono text-sm font-normal text-muted-foreground">({sampleIds.length})</span>
              </h3>
              {expandedSections.samples ? (
                <CaretDown className="w-5 h-5 text-muted-foreground" />
              ) : (
                <CaretRight className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedSections.samples && (
              <div className="mt-3">
                <div className="max-h-64 overflow-y-auto border border-border rounded-lg p-2">
                  {sampleIds.slice(0, 100).map((sampleId) => (
                    <label
                      key={sampleId}
                      className="flex items-center p-2 hover:bg-muted rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSamples.includes(sampleId)}
                        onChange={() => toggleSampleSelection(sampleId)}
                        className="mr-2 accent-primary"
                      />
                      <span className="text-sm text-foreground">{sampleId}</span>
                    </label>
                  ))}
                  {sampleIds.length > 100 && (
                    <p className="text-sm text-muted-foreground p-2">
                      Showing first 100 of {sampleIds.length} samples
                    </p>
                  )}
                </div>
                {selectedSamples.length > 0 && (
                  <div className="mt-2 flex items-center justify-between border border-border rounded px-3 py-2">
                    <span className="text-sm text-muted-foreground">
                      {selectedSamples.length} samples marked for removal
                    </span>
                    <button
                      onClick={() => setSelectedSamples([])}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Filtering Options */}
        <Card className="p-6 mb-6">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-4">
            Filter dataset
          </p>

          <div className="space-y-4">
            {/* Min Variance Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Minimum Gene Variance Threshold
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={minVariance}
                  onChange={(e) => setMinVariance(parseFloat(e.target.value) || 0)}
                  className="flex-1 px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:outline-hidden"
                  placeholder="0.0"
                />
                <span className="text-sm text-muted-foreground">
                  Remove low-variance genes
                </span>
              </div>
            </div>

            {/* Top Variable Genes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Keep Top Variable Genes
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="1"
                  value={topGenes || ''}
                  onChange={(e) =>
                    setTopGenes(e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="flex-1 px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:outline-hidden"
                  placeholder="e.g., 5000"
                />
                <span className="text-sm text-muted-foreground">
                  Select most variable genes
                </span>
              </div>
            </div>

            {/* Apply Filters Button */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                {selectedSamples.length > 0 && (
                  <span className="mr-4">
                    • {selectedSamples.length} samples to remove
                  </span>
                )}
                {minVariance > 0 && (
                  <span className="mr-4">• Min variance: {minVariance}</span>
                )}
                {topGenes && <span>• Top {topGenes} genes</span>}
              </div>
              <Button
                onClick={handleApplyFilters}
                disabled={
                  isFiltering ||
                  (selectedSamples.length === 0 && minVariance === 0 && !topGenes)
                }
                variant="secondary"
              >
                {isFiltering ? (
                  <>
                    <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                    Applying Filters...
                  </>
                ) : (
                  'Apply Filters'
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => navigate('/public-data')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dataset Selection
          </Button>
          <Button
            onClick={handleProceedToSignatures}
          >
            Proceed to Signature Selection
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DatasetPreparation;
